import { activePage, replaceActivePage } from "../native/pages";
import type { EngineDocument, SceneNode, SceneAsset } from "../scene/model";
import { bounds, type Box } from "../native/geometry";
import { cropBounds, cropDocument, cropExpansion } from "../native/crop";
import { createNativeNode, assertNativeDocument } from "../native/document";
import { NativeCanvasRenderer } from "./native-canvas";
import type { ImageStore } from "./image-store";
/** Original destructive crop semantics: independent 1:1 rasters, alpha trim, original layer flags. */
export async function rasterCrop(
  document: EngineDocument,
  area: Box,
  images: ImageStore,
  current: () => boolean,
) {
  const box = cropBounds(area),
    page = activePage(document);
  if (cropExpansion(box, page.size)) return cropDocument(document, box);
  const nodes: SceneNode[] = [],
    assets: SceneAsset[] = [];
  for (const original of page.nodes) {
    if (!current()) throw new Error("Кадрирование отменено.");
    const extent = bounds(original),
      x = Math.max(0, box.x, Math.floor(extent.x - 1)),
      y = Math.max(0, box.y, Math.floor(extent.y - 1));
    const right = Math.min(
        page.size.width,
        box.x + box.width,
        Math.ceil(extent.x + extent.width + 1),
      ),
      bottom = Math.min(
        page.size.height,
        box.y + box.height,
        Math.ceil(extent.y + extent.height + 1),
      );
    if (right <= x || bottom <= y) continue;
    const width = right - x,
      height = bottom - y;
    if (width * height > 32_000_000)
      throw new Error(
        "Слой слишком велик для обрезки. Отключите удаление отсечённых элементов.",
      );
    const canvas = globalThis.document.createElement("canvas"),
      renderer = new NativeCanvasRenderer(canvas, images);
    const node = {
      ...original,
      appearance: { ...original.appearance, visible: true },
    };
    renderer.draw(
      {
        document: {
          ...document,
          pages: [
            {
              ...page,
              nodes: page.nodes.map((n) => (n.id === node.id ? node : n)),
            },
          ],
        },
        camera: { x: -x, y: -y, zoom: 1 },
        width,
        height,
        dpr: 1,
      },
      true,
      new Set([node.id]),
    );
    const ctx = canvas.getContext("2d")!,
      pixels = ctx.getImageData(0, 0, width, height).data;
    let l = width,
      t = height,
      r = -1,
      b = -1;
    for (let py = 0; py < height; py++)
      for (let px = 0; px < width; px++)
        if (pixels[(py * width + px) * 4 + 3]) {
          l = Math.min(l, px);
          t = Math.min(t, py);
          r = Math.max(r, px);
          b = Math.max(b, py);
        }
    if (r >= l) {
      const trim = globalThis.document.createElement("canvas");
      trim.width = r - l + 1;
      trim.height = b - t + 1;
      trim
        .getContext("2d")!
        .drawImage(
          canvas,
          l,
          t,
          trim.width,
          trim.height,
          0,
          0,
          trim.width,
          trim.height,
        );
      const asset = {
        id: crypto.randomUUID(),
        kind: "image" as const,
        source: trim.toDataURL("image/png"),
      };
      assets.push(asset);
      const result = createNativeNode(
        original.id,
        "image",
        x + l - box.x + trim.width / 2,
        y + t - box.y + trim.height / 2,
        trim.width,
        trim.height,
      );
      result.layer = { ...original.layer };
      if (result.layer.clipToBelow) result.layer.clipToBelow = false;
      result.appearance = {
        ...result.appearance,
        opacity: 1,
        visible: original.appearance.visible !== false,
      };
      result.media.image = asset.id;
      nodes.push(result);
    }
    renderer.dispose();
    canvas.width = canvas.height = 0;
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }
  await images.prepare(assets);
  if (!current()) throw new Error("Кадрирование отменено.");
  const result = replaceActivePage(
    { ...document, assets },
    { ...page, size: { width: box.width, height: box.height }, nodes },
  );
  assertNativeDocument(result);
  return result;
}
